/* Auren storefront behaviour, after Averene's: the pieces Dawn doesn't ship.
   - Gallery: round arrows, "1 / 12" pill, thumbnails that follow along
   - Sticky add-to-cart bar (phones and desktop)
   - Bundle cards (Kaching): per-unit pill, free-shipping note, each unit
     keeping its own colour, and the closing offer's cards copied from them
   - "How often?" for products with a Seal subscription plan
   - Customer video reel: under the photos on desktop, and its lightbox
   - Results sliders: counters, arrows and progress bars
   - Delivery dates counted in business days
   - Cart drawer upsell switches
   - Back-to-top button
   Each piece is independent and does nothing when its markup isn't on the page. */

(function () {
  'use strict';

  const onReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const smooth = () => (reduceMotion() ? 'auto' : 'smooth');
  // Set by initMotion; content that arrives later (Kaching's rows, related
  // products) calls it so it can reveal on scroll like the rest.
  let registerReveals = () => {};

  // Kaching renders after the page loads. Calls back once its block exists,
  // or never, on pages without one (gives up after 15 seconds).
  function whenBundles(callback) {
    const found = document.querySelector('.kaching-bundles__block');
    if (found) {
      callback(found);
      return;
    }
    const observer = new MutationObserver(() => {
      const block = document.querySelector('.kaching-bundles__block');
      if (!block) return;
      observer.disconnect();
      callback(block);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 15000);
  }

  /* ------------------------------------------------------------------ */
  /* Back to the bundle cards                                           */
  /* ------------------------------------------------------------------ */

  function scrollToBundles() {
    const target =
      document.querySelector('.kaching-bundles__block') ||
      document.querySelector('variant-selects') ||
      document.querySelector('.product__info-container');
    if (!target) return false;
    const top = target.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top, behavior: smooth() });
    return true;
  }

  function initBundleLinks() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-au-to-bundles], a[href="#MainProduct"]');
      if (!link) return;
      if (scrollToBundles()) event.preventDefault();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Gallery                                                            */
  /* ------------------------------------------------------------------ */

  const ICON_PREV =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#302925" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  const ICON_NEXT =
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#302925" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';

  function initGallery() {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      if (gallery.dataset.auReady) return;
      const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      const list = gallery.querySelector('.product__media-list');
      if (!viewer || !list) return;
      const slides = [...list.querySelectorAll('.product__media-item')];
      if (slides.length < 2) return;
      gallery.dataset.auReady = '1';

      const ui = document.createElement('div');
      ui.className = 'au-gallery-ui';
      ui.innerHTML =
        `<button type="button" class="au-gal-btn au-gal-btn--prev" aria-label="Previous image">${ICON_PREV}</button>` +
        `<button type="button" class="au-gal-btn au-gal-btn--next" aria-label="Next image">${ICON_NEXT}</button>` +
        '<span class="au-gal-count" aria-hidden="true"></span>';
      viewer.style.position = 'relative';
      viewer.appendChild(ui);
      const count = ui.querySelector('.au-gal-count');
      const thumbStrip = gallery.querySelector('.thumbnail-list');

      // On phones Dawn's slider scrolls; on desktop it marks the shown slide is-active.
      const index = () => {
        if (window.matchMedia('(max-width: 749px)').matches && list.clientWidth) {
          return Math.max(0, Math.min(slides.length - 1, Math.round(list.scrollLeft / list.clientWidth)));
        }
        const active = slides.indexOf(list.querySelector('.product__media-item.is-active'));
        return active < 0 ? 0 : active;
      };

      const centreThumb = (id) => {
        if (!thumbStrip) return;
        const item = thumbStrip.querySelector(`[data-target="${id}"]`);
        if (!item) return;
        thumbStrip.querySelectorAll('.thumbnail').forEach((b) => b.removeAttribute('aria-current'));
        const button = item.querySelector('.thumbnail');
        if (button) button.setAttribute('aria-current', 'true');
        const left = item.offsetLeft - (thumbStrip.clientWidth - item.clientWidth) / 2;
        const max = thumbStrip.scrollWidth - thumbStrip.clientWidth;
        thumbStrip.scrollTo({ left: Math.max(0, Math.min(left, max)), behavior: smooth() });
      };

      const update = () => {
        const i = index();
        count.textContent = `${i + 1} / ${slides.length}`;
        return i;
      };

      const show = (i) => {
        const n = slides.length;
        const next = ((i % n) + n) % n;
        const id = slides[next].dataset.mediaId;
        if (window.matchMedia('(max-width: 749px)').matches) {
          list.scrollTo({ left: slides[next].offsetLeft - list.offsetLeft, behavior: smooth() });
        } else if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(id, false);
        }
        centreThumb(id);
        setTimeout(update, 0);
      };

      ui.querySelector('.au-gal-btn--prev').addEventListener('click', () => show(index() - 1));
      ui.querySelector('.au-gal-btn--next').addEventListener('click', () => show(index() + 1));

      let ticking = false;
      list.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const i = update();
            if (window.matchMedia('(max-width: 749px)').matches) centreThumb(slides[i].dataset.mediaId);
          });
        },
        { passive: true }
      );
      new MutationObserver(update).observe(list, { subtree: true, attributes: true, attributeFilter: ['class'] });

      // On phones a thumbnail tap scrolls the photo strip to that photo.
      if (thumbStrip) {
        thumbStrip.addEventListener('click', (event) => {
          const item = event.target.closest('[data-target]');
          if (!item || !window.matchMedia('(max-width: 749px)').matches) return;
          const slide = slides.find((s) => s.dataset.mediaId === item.dataset.target);
          if (!slide) return;
          event.preventDefault();
          event.stopPropagation();
          show(slides.indexOf(slide));
        }, true);
      }
      update();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Sticky add to cart                                                 */
  /* ------------------------------------------------------------------ */

  function initSticky() {
    document.querySelectorAll('[data-au-sticky]').forEach((bar) => {
      if (bar.dataset.auReady) return;
      const button = bar.querySelector('[data-au-sticky-button]');
      if (!document.querySelector(bar.dataset.target) || !button) return;
      bar.dataset.auReady = '1';

      const setVisible = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        button.tabIndex = visible ? 0 : -1;
        const top = document.querySelector('.au-top');
        if (top) top.classList.toggle('au-top--raised', visible);
      };

      // Two modes, so a call to action is on screen from the first scroll:
      // - choose: past the price but not yet at the bundle cards. The button
      //   reads "Choose your bundle" and scrolls to them, rather than adding a
      //   bundle the shopper hasn't seen.
      // - add: past the real button. It reads "Add to cart" and adds.
      // Hidden at the top of the page and while the real button is on screen.
      const priceBox = document.getElementById(`price-${bar.dataset.section}`);
      const hasChoices = () =>
        document.querySelector('.kaching-bundles__block') ||
        document.getElementById(`variant-selects-${bar.dataset.section}`);
      const setMode = (mode) => {
        if (bar.dataset.mode === mode) return;
        bar.dataset.mode = mode;
        button.textContent = mode === 'choose' ? bar.dataset.chooseLabel : bar.dataset.addLabel;
      };
      let ticking = false;
      const update = () => {
        ticking = false;
        const real = document.querySelector(bar.dataset.target);
        if (!real) return;
        const rect = real.getBoundingClientRect();
        if (rect.bottom < 0) {
          setMode('add');
          setVisible(true);
          return;
        }
        // Only while the bundle cards are still below the screen: once they are
        // in view the shopper is choosing, and the bar would cover them.
        const pastPrice = priceBox ? priceBox.getBoundingClientRect().bottom < 0 : window.scrollY > 400;
        const choices = hasChoices();
        if (pastPrice && choices && choices.getBoundingClientRect().top > window.innerHeight) {
          setMode('choose');
          setVisible(true);
          return;
        }
        setVisible(false);
      };
      window.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(update);
        },
        { passive: true }
      );
      update();

      button.addEventListener('click', () => {
        if (bar.dataset.mode === 'choose' && scrollToBundles()) return;
        // Press the real button so the bundle choice and its discount come along.
        const real = document.querySelector(bar.dataset.target);
        if (!real) return;
        if (real.disabled) {
          real.scrollIntoView({ behavior: smooth(), block: 'center' });
          return;
        }
        real.click();
      });

      // The bar quotes what the button will actually add: with Kaching that is
      // the selected bundle (often two units), not one unit's price.
      const titleEl = bar.querySelector('.au-sticky__title');
      const productTitle = titleEl ? titleEl.textContent : '';
      const priceId = `AurenStickyPrice-${bar.dataset.section}`;
      let shown = '';

      const showBundle = () => {
        const selected = document.querySelector('.kaching-bundles__bars .kaching-bundles__bar--selected');
        const priceEl = document.getElementById(priceId);
        if (!selected || !priceEl) return false;
        const name = selected.querySelector('.kaching-bundles__bar-title')?.textContent.trim();
        const price = selected.querySelector('.kaching-bundles__bar-price')?.textContent.trim();
        const save = selected.querySelector('.kaching-bundles__bar-subtitle')?.textContent.trim();
        if (!price) return false;
        // Only touch the bar when the choice changed: rewriting it on every call
        // would itself be a DOM change, and must never feed back into an observer.
        const key = `${name}|${price}|${save}`;
        if (key === shown) return true;
        shown = key;
        const now = document.createElement('span');
        now.textContent = price;
        priceEl.replaceChildren(now);
        if (save) {
          const pill = document.createElement('span');
          pill.className = 'au-sticky__save';
          pill.textContent = save.replace(/!$/, '');
          priceEl.append(pill);
        }
        // The bundle's own name says what the button adds; the photo and the
        // page already say which product it is.
        if (titleEl) titleEl.textContent = name || productTitle;
        return true;
      };

      whenBundles((block) => {
        new MutationObserver(showBundle).observe(block, { subtree: true, attributes: true, attributeFilter: ['class'] });
        showBundle();
      });

      // Without a bundle block, keep the single price in step with the chosen variant.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== bar.dataset.section || !data.html) return;
          if (showBundle()) return;
          const fresh = data.html.getElementById(priceId);
          const current = document.getElementById(priceId);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Bundle cards: per-unit pill, shipping note, closing offer cards    */
  /* ------------------------------------------------------------------ */

  const TRUCK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/></svg>';

  // "$98.60" → { value: 98.6, format: n => "$49.30" }, keeping the shop's symbol,
  // separators and code exactly as Kaching printed them.
  function parseMoney(text) {
    const match = text && text.match(/(\d[\d.,]*)/);
    if (!match) return null;
    const raw = match[1];
    const decimalComma = /,\d{2}$/.test(raw);
    const value = parseFloat(decimalComma ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/,/g, ''));
    if (Number.isNaN(value)) return null;
    const format = (n) => {
      let s = n.toFixed(2);
      if (decimalComma) s = s.replace('.', ',');
      return text.replace(raw, s);
    };
    return { value, format };
  }

  function decorateBars(block) {
    block.querySelectorAll('.kaching-bundles__bars > .kaching-bundles__bar').forEach((bar) => {
      const pricing = bar.querySelector('.kaching-bundles__bar-pricing');
      const price = parseMoney(bar.querySelector('.kaching-bundles__bar-price')?.textContent.trim());
      // A bundle of two or three shows one colour picker per unit.
      const units = bar.querySelectorAll('.kaching-bundles__bar-variant-selector-wrapper').length || 1;
      if (pricing && price && units > 1) {
        // Split in whole cents, rounding half up as Kaching does ($93.99 / 2 → $47.00).
        const text = `${price.format(Math.round(Math.round(price.value * 100) / units) / 100)} each`;
        let pill = pricing.querySelector('.au-unit');
        if (!pill) {
          pill = document.createElement('span');
          pill.className = 'au-unit';
          pricing.appendChild(pill);
        }
        if (pill.textContent !== text) pill.textContent = text;
      }
      const left = bar.querySelector('.kaching-bundles__bar-content-left');
      if (left && block.dataset.auShipNote && !left.querySelector('.au-ship-note')) {
        const note = document.createElement('span');
        note.className = 'au-ship-note';
        note.innerHTML = `${TRUCK}${block.dataset.auShipNote}`;
        left.appendChild(note);
      }
    });
    // An add-on with no discount set in Kaching reads "Save $0.00!"; show nothing instead.
    block.querySelectorAll('.kaching-bundles__checkbox-upsells__upsell .kaching-bundles__bar-subtitle').forEach((sub) => {
      const saving = parseMoney(sub.textContent);
      const zero = /save/i.test(sub.textContent) && !!saving && saving.value === 0;
      if (sub.hidden !== zero) sub.hidden = zero;
    });
  }

  function fillClosingOffer(block) {
    const list = document.querySelector('[data-au-cta-list]');
    if (!list) return;
    const bars = [...block.querySelectorAll('.kaching-bundles__bars > .kaching-bundles__bar')];
    if (!bars.length) return;
    const selectedIndex = bars.findIndex((b) => b.classList.contains('kaching-bundles__bar--selected'));
    const key =
      bars
        .map((b) => `${b.querySelector('.kaching-bundles__bar-title')?.textContent}|${b.querySelector('.kaching-bundles__bar-price')?.textContent}`)
        .join('/') + `#${selectedIndex}`;
    if (list.dataset.key === key) return;
    list.dataset.key = key;

    const rows = bars.map((bar, i) => {
      const li = document.createElement('li');
      const row = document.createElement('button');
      row.type = 'button';
      row.className = i === selectedIndex ? 'au-cta__row is-selected' : 'au-cta__row';
      row.setAttribute('aria-pressed', i === selectedIndex ? 'true' : 'false');
      const nameWrap = document.createElement('span');
      nameWrap.className = 'au-cta__name-wrap';
      const name = document.createElement('span');
      name.className = 'au-cta__name';
      name.textContent = bar.querySelector('.kaching-bundles__bar-title')?.textContent.trim() || '';
      nameWrap.append(name);
      const label = bar.querySelector('.kaching-bundles__bar-most-popular__content')?.textContent.trim();
      if (label) {
        const badge = document.createElement('span');
        badge.className = 'au-cta__badge';
        badge.textContent = label;
        nameWrap.append(badge);
      }
      const priceWrap = document.createElement('span');
      priceWrap.className = 'au-cta__price-wrap';
      const price = document.createElement('span');
      price.className = 'au-cta__price';
      price.textContent = bar.querySelector('.kaching-bundles__bar-price')?.textContent.trim() || '';
      priceWrap.append(price);
      const full = bar.querySelector('.kaching-bundles__bar-full-price')?.textContent.trim();
      if (full && full !== price.textContent) {
        const was = document.createElement('s');
        was.className = 'au-cta__compare';
        was.textContent = full;
        priceWrap.append(was);
      }
      row.append(nameWrap, priceWrap);
      const subtitle = bar.querySelector('.kaching-bundles__bar-subtitle')?.textContent.trim();
      if (subtitle) {
        const sub = document.createElement('span');
        sub.className = 'au-cta__sub';
        sub.textContent = subtitle.replace(/!$/, '');
        row.append(sub);
      }
      // Tapping a card picks that bundle above and takes the shopper to it.
      row.addEventListener('click', () => {
        const current = document.querySelectorAll('.kaching-bundles__bars > .kaching-bundles__bar')[i];
        const target = current && (current.querySelector('input') || current.querySelector('.kaching-bundles__bar-main'));
        if (target && !current.classList.contains('kaching-bundles__bar--selected')) target.click();
        scrollToBundles();
      });
      li.append(row);
      return li;
    });
    list.replaceChildren(...rows);
    registerReveals(list);
    list.dataset.count = String(rows.length);
    list.style.setProperty('--au-cta-cols', String(Math.min(rows.length, 3)));
  }

  // Kaching starts some products (the Eye Patches, where Seal also loads)
  // with no bundle chosen. Choose the "Most popular" one, as the other
  // products and Averene do, unless the shopper has already picked.
  function preselectBundle(block) {
    let touched = false;
    block.addEventListener('pointerdown', () => { touched = true; }, { capture: true, once: true });
    block.addEventListener('keydown', () => { touched = true; }, { capture: true, once: true });
    const started = Date.now();
    const tryPick = () => {
      if (touched || Date.now() - started > 8000) return true;
      const bars = [...block.querySelectorAll('.kaching-bundles__bars > .kaching-bundles__bar')];
      if (bars.length < 2) return false;
      if (bars.some((b) => b.classList.contains('kaching-bundles__bar--selected'))) return false;
      const popular =
        bars.find((b) => /popular/i.test(b.querySelector('.kaching-bundles__bar-most-popular__content')?.textContent || '')) || bars[1];
      const target = popular.querySelector('input') || popular.querySelector('.kaching-bundles__bar-main');
      if (target) target.click();
      return false;
    };
    // Keep checking for a few seconds: Kaching redraws its bars when Seal
    // applies the subscription plan, which can drop the choice again.
    const timer = setInterval(() => { if (tryPick()) clearInterval(timer); }, 400);
    tryPick();
  }

  // Kaching copies a colour picked for one unit onto the other units that still
  // show the old colour. Each unit should keep its own, so note the others just
  // before Kaching handles the change and put them back before the next paint,
  // and again over the next second in case Kaching repeats the copy once the
  // theme has redrawn the product.
  function keepUnitChoices(block) {
    const barSelector = '.kaching-bundles__bars > .kaching-bundles__bar';
    const selectsOf = (barIndex) => {
      const bar = block.querySelectorAll(barSelector)[barIndex];
      return bar ? [...bar.querySelectorAll('.kaching-bundles__bar-variant-selector-wrapper select')] : [];
    };
    let restoring = false;
    let watch = 0; // a newer change, or a click elsewhere, cancels an older watch
    block.addEventListener(
      'change',
      (event) => {
        const changed = event.target;
        if (restoring || !(changed instanceof HTMLSelectElement)) return;
        const barIndex = [...block.querySelectorAll(barSelector)].findIndex((bar) => bar.contains(changed));
        if (barIndex < 0) return;
        const selects = selectsOf(barIndex);
        const changedIndex = selects.indexOf(changed);
        if (changedIndex < 0 || selects.length < 2) return;
        const wanted = selects.map((select) => select.value);
        const mine = ++watch;
        const restore = () => {
          if (mine !== watch) return;
          restoring = true;
          selectsOf(barIndex).forEach((select, i) => {
            if (i === changedIndex || select.value === wanted[i]) return;
            select.value = wanted[i];
            select.dispatchEvent(new Event('change', { bubbles: true }));
          });
          restoring = false;
        };
        requestAnimationFrame(restore);
        [250, 700, 1300].forEach((ms) => setTimeout(restore, ms));
      },
      true
    );
    block.addEventListener('pointerdown', (event) => { if (!(event.target instanceof HTMLSelectElement)) watch++; }, true);
    block.addEventListener('keydown', (event) => { if (!(event.target instanceof HTMLSelectElement)) watch++; }, true);
  }

  function initBundles() {
    whenBundles((block) => {
      preselectBundle(block);
      keepUnitChoices(block);
      // Products under the free-shipping line don't get the note.
      const info = block.closest('.product__info-container');
      const note = info && info.querySelector('[data-au-ship-note]');
      if (note) block.dataset.auShipNote = note.dataset.auShipNote;
      let queued = false;
      const run = () => {
        queued = false;
        decorateBars(block);
        fillClosingOffer(block);
      };
      // Kaching re-renders its bars when a choice changes; re-apply after it.
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(run);
      }).observe(block, { childList: true, subtree: true, characterData: true });
      run();
    });
  }

  /* ------------------------------------------------------------------ */
  /* "How often?" over Seal Subscriptions                               */
  /* ------------------------------------------------------------------ */

  function initPlan() {
    const plan = document.querySelector('[data-au-plan]');
    if (!plan) return;
    const one = plan.querySelector('[data-au-plan-one]');
    const sub = plan.querySelector('[data-au-plan-sub]');
    const note = plan.querySelector('[data-au-plan-note]');
    const interval = plan.querySelector('[data-au-plan-interval]');
    const host = plan.closest('product-info') || document.body;

    const planInput = () => host.querySelector('input[name="selling_plan"]');
    const options = () => [...host.querySelectorAll('.sls-option-container')];
    const optionFor = (subscribe) => {
      const all = options();
      if (!all.length) return null;
      return subscribe
        ? all.find((o) => o.hasAttribute('data-selling-plan-group')) || all[1] || null
        : all.find((o) => !o.hasAttribute('data-selling-plan-group')) || all[0];
    };
    const subscribing = () => {
      const input = planInput();
      return !!(input && input.value);
    };
    // Seal prints each option's price; show it under each half.
    const priceOf = (option) => {
      if (!option) return '';
      const price = option.querySelector('.sls-price, [class*="price"]');
      const badge = option.querySelector('.sls-savings-badge');
      const p = price ? price.textContent.replace(/\s+/g, ' ').trim() : '';
      const b = badge ? badge.textContent.replace(/\s+/g, ' ').trim() : '';
      return [p, b].filter(Boolean).join(' · ');
    };

    // With Subscribe & save on, the price under the title is the one-unit
    // subscription price, as on Averene; the struck price and saving follow.
    const updateHeadline = (on, pct) => {
      const box = host.querySelector('[id^="price-"] .au-price');
      const now = box && box.querySelector('.au-price__now');
      if (!now) return;
      const was = box.querySelector('.au-price__was');
      const save = box.querySelector('.au-price__save');
      if (!now.dataset.base) now.dataset.base = now.textContent.trim();
      if (save && !save.dataset.base) save.dataset.base = save.textContent.trim();
      let text = now.dataset.base;
      let saveText = save ? save.dataset.base : '';
      const base = parseMoney(now.dataset.base);
      if (on && pct > 0 && base) {
        const sub = base.value * (1 - pct / 100);
        text = base.format(sub);
        const compare = was && parseMoney(was.textContent.trim());
        if (compare && compare.value > 0) saveText = `Save ${Math.round((1 - sub / compare.value) * 100)}%`;
      }
      if (now.textContent !== text) now.textContent = text;
      if (save && save.textContent !== saveText) save.textContent = saveText;
    };

    let lastKey = '';
    const sync = () => {
      const on = subscribing();
      const badge = optionFor(true)?.querySelector('.sls-savings-badge');
      const pct = badge ? parseInt(badge.textContent.replace(/[^\d]/g, ''), 10) || 0 : 0;
      updateHeadline(on, pct);
      const oneMeta = priceOf(optionFor(false));
      const subMeta = priceOf(optionFor(true));
      const select = host.querySelector('select[name="subs_interval"], .sls-select select');
      const intervalText = select && select.options[select.selectedIndex] ? select.options[select.selectedIndex].text.trim() : '';
      const key = `${on}|${oneMeta}|${subMeta}|${intervalText}`;
      if (key === lastKey) return;
      lastKey = key;
      one.classList.toggle('is-on', !on);
      sub.classList.toggle('is-on', on);
      one.querySelector('input').checked = !on;
      sub.querySelector('input').checked = on;
      one.querySelector('[data-au-plan-meta]').textContent = oneMeta;
      sub.querySelector('[data-au-plan-meta]').textContent = subMeta;
      if (note) {
        note.hidden = !on;
        if (interval) interval.textContent = intervalText ? `Delivered ${intervalText.toLowerCase().startsWith('every') ? '' : 'every '}${intervalText}` : 'Delivered monthly';
      }
    };

    const choose = (subscribe) => {
      const option = optionFor(subscribe);
      const radio = option && (option.querySelector('[role="radio"]') || option.querySelector('input[type="radio"]') || option);
      if (radio) radio.click();
      setTimeout(sync, 60);
      setTimeout(sync, 400);
    };
    one.addEventListener('click', (event) => {
      event.preventDefault();
      choose(false);
    });
    sub.addEventListener('click', (event) => {
      event.preventDefault();
      choose(true);
    });

    // Seal draws its widget a moment after the page; show ours once it exists.
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (options().length >= 2) {
        clearInterval(timer);
        plan.hidden = false;
        host.classList.add('au-has-plan');
        // Seal sometimes starts with neither option applied, which shows the
        // subscription as chosen but charges one-time. Apply its default.
        const anyChecked = options().some((o) => o.querySelector('[role="radio"][aria-checked="true"]'));
        if (!anyChecked && !subscribing()) choose(true);
        sync();
        new MutationObserver(() => requestAnimationFrame(sync)).observe(
          host.querySelector('.sealsubs-target-element') || host,
          { subtree: true, attributes: true, childList: true, characterData: true }
        );
        host.addEventListener('change', () => setTimeout(sync, 80));
      } else if (tries > 60) {
        clearInterval(timer);
      }
    }, 250);
  }

  /* ------------------------------------------------------------------ */
  /* Video reel and lightbox                                            */
  /* ------------------------------------------------------------------ */

  // On desktop the reel belongs under the photos, as on Averene.
  function placeReels() {
    const media = document.querySelector('.product__media-wrapper');
    document.querySelectorAll('[data-au-reel]').forEach((reel) => {
      if (!reel.auHome) {
        reel.auHome = reel.parentNode;
        reel.auMark = document.createComment('au-reel');
        reel.parentNode.insertBefore(reel.auMark, reel);
      }
      const desktop = window.matchMedia('(min-width: 750px)').matches;
      if (desktop && media) {
        if (reel.parentNode !== media) media.appendChild(reel);
      } else if (reel.parentNode !== reel.auHome && reel.auMark.parentNode) {
        reel.auMark.parentNode.insertBefore(reel, reel.auMark.nextSibling);
      }
    });
  }

  let lightbox;
  let lastFocus;

  function getLightbox() {
    if (lightbox) return lightbox;
    lightbox = document.createElement('div');
    lightbox.className = 'au-lb';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Customer video');
    lightbox.innerHTML =
      '<div class="au-lb__inner">' +
      '<button type="button" class="au-lb__close" data-lb-close aria-label="Close video"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#302925" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button>' +
      '<div class="au-lb__frame"><video playsinline controls preload="none"></video></div>' +
      '<p class="au-lb__hint">Click anywhere outside the video to close</p>' +
      '</div>';
    document.body.appendChild(lightbox);

    const close = () => {
      const video = lightbox.querySelector('video');
      video.pause();
      video.removeAttribute('src');
      video.load();
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    };
    lightbox.addEventListener('click', (event) => {
      if (event.target === lightbox || event.target.closest('[data-lb-close]')) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && lightbox.classList.contains('is-open')) close();
    });
    return lightbox;
  }

  function openVideo(src, trigger) {
    const box = getLightbox();
    const video = box.querySelector('video');
    lastFocus = trigger;
    video.src = src;
    video.muted = false;
    box.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    box.querySelector('[data-lb-close]').focus();
    const playing = video.play();
    if (playing && playing.catch) playing.catch(() => {});
  }

  function initReels() {
    placeReels();
    const mq = window.matchMedia('(min-width: 750px)');
    if (mq.addEventListener) mq.addEventListener('change', placeReels);

    document.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-au-reel-open]');
      if (tile) {
        event.preventDefault();
        openVideo(tile.dataset.auReelOpen, tile);
        return;
      }
      const arrow = event.target.closest('[data-au-reel-prev], [data-au-reel-next]');
      if (!arrow) return;
      const track = arrow.closest('[data-au-reel]')?.querySelector('[data-au-reel-track]');
      if (!track) return;
      const tile0 = track.querySelector('.au-reel__tile');
      const step = (tile0 ? tile0.getBoundingClientRect().width : 197) + 16;
      track.scrollBy({ left: arrow.hasAttribute('data-au-reel-next') ? step : -step, behavior: smooth() });
    });

    // Tiles load their first frame only when they come into view.
    const previews = document.querySelectorAll('[data-au-reel-preview]');
    if (!previews.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.preload = 'metadata';
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '200px' }
    );
    previews.forEach((v) => io.observe(v));
  }

  /* ------------------------------------------------------------------ */
  /* Results sliders                                                    */
  /* ------------------------------------------------------------------ */

  // Product page: several photos in view, a counter and a sliding bar.
  function initResults() {
    document.querySelectorAll('[data-au-res]').forEach((section) => {
      if (section.dataset.auReady) return;
      const strip = section.querySelector('[data-au-res-strip]');
      if (!strip) return;
      section.dataset.auReady = '1';
      const slides = [...strip.children];
      const bar = section.querySelector('[data-au-res-bar]');
      const count = section.querySelector('[data-au-res-count]');
      const index = () => {
        const mid = strip.scrollLeft + strip.clientWidth / 2;
        let best = 0;
        let dist = Infinity;
        slides.forEach((s, i) => {
          const d = Math.abs(s.offsetLeft - strip.offsetLeft + s.offsetWidth / 2 - mid);
          if (d < dist) {
            dist = d;
            best = i;
          }
        });
        return best;
      };
      const go = (i) => {
        const n = slides.length;
        const next = ((i % n) + n) % n;
        strip.scrollTo({ left: slides[next].offsetLeft - strip.offsetLeft - (strip.clientWidth - slides[next].offsetWidth) / 2, behavior: smooth() });
      };
      const paint = () => {
        if (count) count.textContent = `${index() + 1} / ${slides.length}`;
        if (bar) {
          const width = Math.max(10, Math.min(100, (strip.clientWidth / strip.scrollWidth) * 100));
          const max = strip.scrollWidth - strip.clientWidth;
          const pos = max > 0 ? strip.scrollLeft / max : 0;
          bar.style.width = `${width}%`;
          bar.style.left = `${pos * (100 - width)}%`;
        }
      };
      section.querySelector('[data-au-res-prev]')?.addEventListener('click', () => go(index() - 1));
      section.querySelector('[data-au-res-next]')?.addEventListener('click', () => go(index() + 1));
      strip.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          go(index() + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          go(index() - 1);
        }
      });
      strip.addEventListener('scroll', () => requestAnimationFrame(paint), { passive: true });
      window.addEventListener('resize', paint);
      paint();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Delivery dates                                                     */
  /* ------------------------------------------------------------------ */

  function addBusinessDays(date, days) {
    const d = new Date(date.getTime());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }

  function initDelivery() {
    const lang = document.documentElement.lang || undefined;
    const format = (d) => d.toLocaleDateString(lang, { weekday: 'long', month: 'long', day: 'numeric' });
    document.querySelectorAll('[data-au-delivery]').forEach((line) => {
      const min = parseInt(line.dataset.min, 10);
      const max = parseInt(line.dataset.max, 10);
      const text = line.querySelector('[data-au-delivery-text]');
      if (!text || !text.dataset.template || Number.isNaN(min) || Number.isNaN(max)) return;
      const today = new Date();
      text.innerHTML = text.dataset.template
        .replace('[start]', format(addBusinessDays(today, min)))
        .replace('[end]', format(addBusinessDays(today, max)));
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart drawer upsell switches                                        */
  /* ------------------------------------------------------------------ */

  function initUpsells() {
    document.addEventListener('click', async (event) => {
      const toggle = event.target.closest('[data-au-upsell]');
      if (!toggle || toggle.getAttribute('aria-busy') === 'true') return;
      event.preventDefault();

      const drawer = document.querySelector('cart-drawer');
      const on = toggle.getAttribute('aria-checked') === 'true';
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((s) => s.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      toggle.setAttribute('aria-busy', 'true');
      try {
        const url = on ? `${window.routes.cart_change_url}.js` : `${window.routes.cart_add_url}.js`;
        const body = on
          ? { id: toggle.dataset.lineKey, quantity: 0, sections, sections_url: window.location.pathname }
          : {
              // The hidden _au_upsell property marks the line as added by its switch,
              // so the drawer shows it as a switched-on row, not a second cart line.
              items: [{ id: Number(toggle.dataset.variantId), quantity: 1, properties: { _au_upsell: '1' } }],
              sections,
              sections_url: window.location.pathname,
            };
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || 'Cart update failed');

        if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
          drawer.renderContents(state);
        } else {
          window.location.reload();
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auren-upsell', cartData: state });
        }
      } catch (error) {
        toggle.removeAttribute('aria-busy');
        console.error(error);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Motion: scroll reveals and word-by-word headings                   */
  /* ------------------------------------------------------------------ */

  // [selector, ms added before this item when several enter together]
  const REVEAL_FADE = [
    ['.au-packs__eyebrow, .au-res__kicker, .au-steps__kicker, .au-backed__kicker, .au-reel__kicker, .au-patent__kicker', 60],
    ['.au-press__label', 0],
    ['.au-packs__intro > p:not(.au-packs__eyebrow), .au-res__intro, .au-steps__intro, .au-backed__intro, .au-why__intro, .au-lift__intro, .au-story__intro, .au-health__intro, .au-patent__intro, .au-cta__text', 80],
    ['.au-story__figure, .au-story__card, .au-lift__rail, .au-res__viewport, .au-quote-band__inner, .au-stats, .au-patent__media', 0],
    ['.au-packs__card, .au-steps__card, .au-pro, .au-scene, .au-lift__stat, .au-review, .au-health__moment, .au-patent__fact, .au-cta__list > li', 80],
    ['.au-res__bar, .au-res__cta, .au-lift__cta, .au-lift__note, .au-cta__actions, .contact form', 80],
    ['.au-reel__row > li', 60],
    ['.product-card-wrapper, .collection-card-wrapper', 60],
  ];
  const REVEAL_SPLIT =
    '.au-packs__intro h2, .au-ben__title, .au-story__title, .au-lift__title, .au-res__title, .au-steps__title, .au-backed__title, .au-why__title, .au-health__title, .au-patent__title, .au-cta__title, .au-reel__title, .contact .title, .related-products__heading';
  const MAX_STAGGER = 480;

  // True when a word runs straight into the next tag with no space between
  // (risk-<strong>free</strong>). Split into word blocks, the line could then
  // break there, where it can't now, so such a heading fades in whole.
  function hasJoinedWords(el) {
    const blockOf = (node) => {
      let parent = node.parentElement;
      while (parent !== el && getComputedStyle(parent).display === 'inline') parent = parent.parentElement;
      return parent;
    };
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let previous = null;
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === 'BR') previous = null;
        continue;
      }
      if (!node.textContent) continue;
      if (previous && /\S$/.test(previous.textContent) && /^\S/.test(node.textContent) && blockOf(previous) === blockOf(node)) {
        return true;
      }
      previous = node;
    }
    return false;
  }

  // Wraps each word in a span (keeping <strong> and the like) so the words
  // can rise in turn. The text itself doesn't change.
  function splitWords(el) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let index = 0;
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const parts = node.textContent.split(/(\s+)/);
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }
        const word = document.createElement('span');
        word.className = 'au-w';
        word.style.setProperty('--i', String(Math.min(index, 10)));
        word.textContent = part;
        fragment.appendChild(word);
        index += 1;
      });
      node.parentNode.replaceChild(fragment, node);
    });
  }

  function initMotion() {
    if (reduceMotion() || !('IntersectionObserver' in window)) return;
    const pending = new Set();

    // Shows an element; a fade-up item is then handed back to its own styles
    // (hover and the rest) once it has arrived.
    const reveal = (el) => {
      pending.delete(el);
      el.classList.add('is-in');
      if (!el.hasAttribute('data-au-reveal')) return;
      const wait = (parseFloat(el.style.getPropertyValue('--au-delay')) || 0) + 700;
      setTimeout(() => {
        el.removeAttribute('data-au-reveal');
        el.classList.remove('is-in');
        el.style.removeProperty('--au-delay');
      }, wait);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entering = [];
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          // Carried above the screen unseen: show it at once, out of the queue.
          if (entry.boundingClientRect.bottom <= 0) reveal(entry.target);
          else entering.push(entry.target);
        });
        // Everything that enters together goes in page order, a beat apart;
        // the cells of one comparison row share their beat.
        entering.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
        let delay = 0;
        let lastGroup = null;
        entering.forEach((el, i) => {
          const group = el.dataset.auGroup || null;
          if (i > 0 && !(group && group === lastGroup)) {
            delay = Math.min(delay + Number(el.dataset.auStep || 0), MAX_STAGGER);
          }
          lastGroup = group;
          el.style.setProperty('--au-delay', `${delay}ms`);
          reveal(el);
        });
      },
      // The watched area reaches far above the screen, so anything carried
      // past it unseen (a fling, a jump back to a saved scroll position) is
      // shown as well, rather than waiting until it is scrolled back to.
      { rootMargin: '100000px 0px -8% 0px', threshold: 0.12 }
    );

    const register = (el, step, kind, group) => {
      if (el.dataset.auSeen) return;
      el.dataset.auSeen = '1';
      const rect = el.getBoundingClientRect();
      // Already on screen, above it, or hidden: just show, no animation.
      if (rect.bottom <= 0 || rect.top < window.innerHeight || (!rect.width && !rect.height)) return;
      if (kind === 'split' && !hasJoinedWords(el)) {
        splitWords(el);
        el.setAttribute('data-au-split', '');
      } else {
        el.setAttribute('data-au-reveal', '');
      }
      if (step) el.dataset.auStep = String(step);
      if (group) el.dataset.auGroup = group;
      pending.add(el);
      observer.observe(el);
    };

    // A card in a swipe row comes in with the whole row: on its own it could
    // be swiped into view before it appears, and its 16px drop would make
    // the row scroll up and down too.
    const swipeRow = (el) => {
      const parent = el.parentElement;
      return parent && /auto|scroll/.test(getComputedStyle(parent).overflowX) ? parent : null;
    };

    registerReveals = (scope = document) => {
      REVEAL_FADE.forEach(([selector, step]) => {
        scope.querySelectorAll(selector).forEach((el) => register(swipeRow(el) || el, step, 'fade'));
      });
      scope.querySelectorAll(REVEAL_SPLIT).forEach((el) => register(el, 80, 'split'));
      // Comparison table: each row's three cells arrive together, 50ms apart.
      scope.querySelectorAll('.au-why__card').forEach((card, cardIndex) => {
        [...card.children].forEach((cell, i) => {
          register(cell, 50, 'fade', `why-${cardIndex}-${Math.floor(i / 3)}`);
        });
      });
    };

    document.documentElement.classList.add('au-reveal-on');
    registerReveals();

    // Related products arrive by fetch; reveal them like the rest.
    document.querySelectorAll('product-recommendations').forEach((host) => {
      const watch = new MutationObserver(() => {
        if (!host.querySelector('.product-card-wrapper')) return;
        watch.disconnect();
        registerReveals(host);
      });
      watch.observe(host, { childList: true, subtree: true });
    });

    // Never leave anything hidden: a sweep shows whatever is on screen but
    // still waiting, and printing shows everything.
    const sweep = () => {
      pending.forEach((el) => {
        if (!el.isConnected) {
          pending.delete(el);
          return;
        }
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0) {
          observer.unobserve(el);
          reveal(el);
        }
      });
    };
    setTimeout(sweep, 3000);
    window.addEventListener('beforeprint', () => {
      pending.forEach((el) => {
        observer.unobserve(el);
        reveal(el);
      });
    });

    // iOS Safari applies :active (the button press) only with a touch listener.
    document.addEventListener('touchstart', () => {}, { passive: true });
  }

  /* ------------------------------------------------------------------ */
  /* Figures that count up as they come into view                       */
  /* ------------------------------------------------------------------ */

  // The real figure is always in the HTML. A band of figures below the screen
  // when the page opens is reset to 0 and counted up once, when the whole band
  // has risen into the top two-thirds of the screen: read there, not glimpsed
  // at the bottom edge as it fades in. The figures follow each other a beat
  // apart, and a ring around one fills as it climbs. Figures already on
  // screen, and all of them for reduced motion, just show.
  function initCountUp() {
    const figures = [...document.querySelectorAll('[data-au-count]:not([data-au-counted])')];
    if (!figures.length || reduceMotion() || !('IntersectionObserver' in window)) return;
    const DURATION = 1800;
    const STAGGER = 180;
    const ringOf = (el) => el.closest('.au-ring');
    const paint = (el, value) => {
      const decimals = (el.dataset.auCount.split('.')[1] || '').length;
      el.textContent = value.toFixed(decimals);
      const arc = ringOf(el)?.querySelector('.au-ring__arc');
      if (!arc) return;
      const length = parseFloat(arc.getAttribute('stroke-dasharray'));
      arc.style.strokeDashoffset = String(length * (1 - Math.min(value, 100) / 100));
      // An empty ring's round cap would still draw a dot.
      arc.style.strokeOpacity = value > 0 ? '' : '0';
    };
    const finish = (el) => {
      el.textContent = el.dataset.auCount;
      const ring = ringOf(el);
      if (!ring) return;
      const arc = ring.querySelector('.au-ring__arc');
      arc.style.removeProperty('stroke-dashoffset');
      arc.style.removeProperty('stroke-opacity');
      ring.classList.remove('is-counting');
    };
    const count = (el, delay) => {
      const target = parseFloat(el.dataset.auCount);
      setTimeout(() => {
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / DURATION);
          paint(el, target * (1 - Math.pow(1 - t, 3)));
          if (t < 1) requestAnimationFrame(step);
          else finish(el);
        };
        requestAnimationFrame(step);
      }, delay);
    };
    const bands = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.98) return;
          observer.unobserve(entry.target);
          const members = bands.get(entry.target) || [];
          // Carried past unseen (a fling, a jump): just show the figures.
          if (entry.boundingClientRect.bottom <= 0) members.forEach(finish);
          else members.forEach((el, i) => count(el, 250 + i * STAGGER));
        });
      },
      // The bottom third of the screen doesn't count. As with the reveals, the
      // watched area reaches far above the screen, so nothing is left at 0.
      { rootMargin: '100000px 0px -33% 0px', threshold: 0.98 }
    );
    figures.forEach((el) => {
      el.dataset.auCounted = '1';
      if (!Number.isFinite(parseFloat(el.dataset.auCount))) return;
      const band = el.closest('[data-au-count-group]') || el;
      const rect = band.getBoundingClientRect();
      if (rect.top < window.innerHeight || (!rect.width && !rect.height)) return;
      paint(el, 0);
      ringOf(el)?.classList.add('is-counting');
      if (!bands.has(band)) {
        bands.set(band, []);
        observer.observe(band);
      }
      bands.get(band).push(el);
    });
    window.addEventListener('beforeprint', () => figures.forEach(finish));
  }

  /* ------------------------------------------------------------------ */
  /* The research section: the photo follows the moment being read      */
  /* ------------------------------------------------------------------ */

  // The moments are three stops in one night's reading. As each one reaches the
  // middle of the screen it becomes the current one: its marker lights, the line
  // above it fills, and on desktop the tall photo beside the list changes to its
  // photo. Without this script every marker is lit and the first photo shows.
  function initHealth() {
    document.querySelectorAll('[data-au-health]').forEach((section) => {
      if (section.dataset.auReady || !('IntersectionObserver' in window)) return;
      const moments = [...section.querySelectorAll('[data-au-health-moment]')];
      if (!moments.length) return;
      section.dataset.auReady = '1';
      const photos = [...section.querySelectorAll('[data-au-health-frame] img')];
      const LINE = 0.51;
      let shown = -1;
      const update = () => {
        const line = window.innerHeight * LINE;
        let current = 0;
        moments.forEach((moment, i) => {
          if (moment.getBoundingClientRect().top <= line) current = i;
        });
        if (current === shown) return;
        shown = current;
        moments.forEach((moment, i) => {
          moment.classList.toggle('is-reached', i <= current);
          moment.classList.toggle('is-current', i === current);
        });
        photos.forEach((photo, i) => photo.classList.toggle('is-active', i === current));
        section.classList.add('is-live');
      };
      // A thin band just under the middle of the screen: a moment's top edge
      // crossing it, in either direction, re-runs the check.
      const io = new IntersectionObserver(update, { rootMargin: '-50% 0px -49% 0px' });
      moments.forEach((moment) => io.observe(moment));
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  }

  /* ------------------------------------------------------------------ */
  /* Desktop: the gallery column travels with the buy box               */
  /* ------------------------------------------------------------------ */

  // Beside the buy box, the gallery and its video reel end well before the
  // bundles, add to cart and tabs do, which left a tall empty gap. The column
  // now stays in view while the buy box scrolls: under the header when it
  // fits on screen, or by its bottom edge, above the sticky bar, when it is
  // taller, so every part of it is seen on the way down.
  function initMediaColumn() {
    const media = document.querySelector('.au .product__media-wrapper');
    if (!media || media.dataset.auFollow) return;
    media.dataset.auFollow = '1';
    const twoColumns = window.matchMedia('(min-width: 750px)');
    const GAP = 20;
    let queued = false;
    const place = () => {
      queued = false;
      if (!twoColumns.matches) {
        media.classList.remove('au-media-follow');
        return;
      }
      // The class first, so the height measured is the column's own.
      media.classList.add('au-media-follow');
      const header = document.querySelector('.section-header');
      const bar = document.querySelector('.au-sticky');
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const barHeight = bar ? bar.getBoundingClientRect().height : 0;
      const top = Math.min(headerHeight + GAP, window.innerHeight - barHeight - GAP - media.offsetHeight);
      media.style.setProperty('--au-media-top', `${Math.round(top)}px`);
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(place);
    };
    place();
    window.addEventListener('resize', queue, { passive: true });
    if (twoColumns.addEventListener) twoColumns.addEventListener('change', queue);
    // The reel moves in, photos load and variants swap: re-measure each time.
    if ('ResizeObserver' in window) new ResizeObserver(queue).observe(media);
  }

  /* ------------------------------------------------------------------ */
  /* Back to top                                                        */
  /* ------------------------------------------------------------------ */

  function initTop() {
    if (document.querySelector('.au-top')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'au-top';
    button.setAttribute('aria-label', 'Back to top');
    button.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 15l6-6 6 6"/></svg>';
    button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: smooth() }));
    document.body.appendChild(button);
    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          button.classList.toggle('is-visible', window.scrollY > window.innerHeight);
        });
      },
      { passive: true }
    );
  }

  onReady(() => {
    initBundleLinks();
    initGallery();
    initSticky();
    initBundles();
    initPlan();
    initReels();
    initMediaColumn();
    initHealth();
    initResults();
    initDelivery();
    initUpsells();
    initTop();
    initMotion();
    initCountUp();
  });

  // The theme editor re-renders sections in place; re-run what they need.
  document.addEventListener('shopify:section:load', (event) => {
    registerReveals(event.target);
    initCountUp();
    initGallery();
    initSticky();
    initReels();
    initMediaColumn();
    initHealth();
    initResults();
    initDelivery();
  });
})();
